import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import './Auth.css';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

export default function CharacterSelect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const token = params.get('token');
  const error = params.get('error');

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectError, setSelectError] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      setPayload(JSON.parse(atob(base64)));
    } catch (_) { navigate('/login?error=discord_error'); }
  }, [token, navigate]);

  const handleSelect = async (characterId) => {
    setLoading(true);
    setSelectError(null);
    try {
      const res = await axios.post('/api/auth/select-character',
        { characterId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      login(res.data.token);
      navigate('/shop');
    } catch (e) {
      setSelectError(e.response?.data?.error || 'Erreur lors de la sélection du personnage');
      setLoading(false);
    }
  };

  const noCharacter = error === 'no_license' || error === 'no_character' || payload?.noCharacter;

  if (!payload && !noCharacter) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 32, color: 'var(--primary)', marginBottom: 16 }} />
          <p style={{ color: 'var(--text-muted)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        {/* Logo */}
        <div className="auth-logo">
          <img src="/logo.png" alt="FastBox" className="auth-logo-img" onError={e => { e.target.style.display = 'none'; }} />
          <span className="auth-logo-text">Fast Box</span>
        </div>

        {/* Discord user */}
        {payload && !payload.noCharacter && (
          <div className="discord-user-info">
            <img
              src={payload.avatar
                ? `https://cdn.discordapp.com/avatars/${payload.discordId}/${payload.avatar}.png?size=64`
                : '/logo.png'}
              alt="Avatar"
              className="discord-avatar"
              onError={e => { e.target.src = '/logo.png'; }}
            />
            <div>
              <p className="discord-username">{payload.username}</p>
              <span className="badge badge-red" style={{ fontSize: 11 }}>
                <i className="fa-brands fa-discord" /> Vérifié
              </span>
            </div>
          </div>
        )}

        {/* Aucun personnage */}
        {noCharacter ? (
          <>
            <div className="auth-error-icon">
              <i className="fa-solid fa-user-slash" style={{ color: 'var(--text-muted)' }} />
            </div>
            <h2 className="auth-title">Aucun compte bancaire</h2>
            <p className="auth-desc">
              {error === 'no_license'
                ? 'Votre compte Discord n\'est pas lié à une license FiveM. Connectez-vous au moins une fois en jeu.'
                : 'Aucun personnage avec un compte Fleeca n\'est associé à votre Discord.'}
            </p>
            <a href="/api/auth/discord" className="btn-primary auth-btn" style={{ justifyContent: 'center' }}>
              <i className="fa-brands fa-discord" /> Réessayer
            </a>
          </>
        ) : (
          <>
            <h2 className="auth-title">Choisissez un personnage</h2>
            <p className="auth-desc">Sélectionnez le personnage avec lequel vous souhaitez commander.</p>

            {selectError && (
              <div className="auth-error-banner">
                <i className="fa-solid fa-triangle-exclamation" />
                {selectError}
              </div>
            )}

            <div className="characters-grid">
              {payload?.characters?.map(character => (
                <button
                  key={character.id}
                  className="character-card"
                  onClick={() => handleSelect(character.id)}
                  disabled={loading}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fa-solid fa-user" style={{ color: 'var(--primary)', fontSize: 14 }} />
                    </div>
                    <span className="character-name">{character.name}</span>
                  </div>
                  <span className="character-iban">{character.iban}</span>
                  <span className="character-balance">{formatAmount(character.balance)}</span>
                  <span className="character-job">
                    <i className="fa-solid fa-briefcase" style={{ fontSize: 10 }} /> {character.job || 'sans emploi'}
                  </span>
                </button>
              ))}
            </div>

            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 14 }}>
                <i className="fa-solid fa-spinner spinner" />
                Connexion en cours...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
